import React, { useState } from 'react';
import { OsintTask } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityBadge } from '../components/PriorityBadge';
import { SummaryCard } from '../components/SummaryCard';
import { GlobeIcon, SearchIcon, PlusIcon, ActivityIcon, CheckCircleIcon, ClockIcon, EyeIcon, DatabaseIcon } from 'lucide-react';
interface OsintPageProps {
  tasks: OsintTask[];
}
export function OsintPage({
  tasks
}: OsintPageProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('Semua Status');
  const [priorityFilter, setPriorityFilter] = useState('Semua Prioritas');
  const filteredTasks = tasks.filter(task => {
    const matchSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase()) || task.code.toLowerCase().includes(searchTerm.toLowerCase()) || task.subjectName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'Semua Status' || task.status === statusFilter;
    const matchPriority = priorityFilter === 'Semua Prioritas' || task.priority === priorityFilter;
    return matchSearch && matchStatus && matchPriority;
  });
  // Stats
  const activeCount = tasks.filter(t => t.status === 'Berjalan').length;
  const plannedCount = tasks.filter(t => t.status === 'Direncanakan').length;
  const completedCount = tasks.filter(t => t.status === 'Selesai').length;
  const secretCount = tasks.filter(t => t.classification === 'Secret').length;
  const handleAdd = () => alert('Fitur tambah tugas OSINT akan tersedia pada versi berikutnya.');
  const handleView = (id: string) => alert(`Membuka detail tugas OSINT: ${id}`);
  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'Public':
        return 'text-[#00FF88] border-[#00FF88]/30 bg-[#00FF88]/10';
      case 'Restricted':
        return 'text-[#00D4FF] border-[#00D4FF]/30 bg-[#00D4FF]/10';
      case 'Confidential':
        return 'text-[#FFB800] border-[#FFB800]/30 bg-[#FFB800]/10';
      case 'Secret':
        return 'text-[#FF3B3B] border-[#FF3B3B]/30 bg-[#FF3B3B]/10';
      default:
        return 'text-[#94A3B8] border-[#64748B]/30 bg-[#64748B]/10';
    }
  };
  return <div className="space-y-6 flex flex-col h-full" data-id="element-769">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-770">
        <h2 className="text-2xl font-bold text-[#E2E8F0] uppercase tracking-wide flex items-center" data-id="element-771">
          <GlobeIcon className="w-6 h-6 mr-3 text-[#00FF88]" data-id="element-772" />
          OPEN SOURCE INTELLIGENCE (OSINT)
        </h2>
        <button onClick={handleAdd} className="flex items-center px-4 py-2 bg-[#00FF88] text-[#0B0F1A] rounded hover:bg-[#00FF88]/90 transition-colors shadow-sm font-bold text-sm uppercase glow-green" data-id="element-773">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-774" />
          TAMBAH TUGAS
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-775">
        <SummaryCard title="Tugas Berjalan" value={activeCount} icon={<ActivityIcon className="w-6 h-6" data-id="element-777" />} iconBgColor="bg-[#00D4FF]/10" iconColor="text-[#00D4FF]" borderColor="border-t-[#00D4FF]" data-id="element-776" />
        <SummaryCard title="Direncanakan" value={plannedCount} icon={<ClockIcon className="w-6 h-6" data-id="element-779" />} iconBgColor="bg-[#A855F7]/10" iconColor="text-[#A855F7]" borderColor="border-t-[#A855F7]" data-id="element-778" />
        <SummaryCard title="Klasifikasi Secret" value={secretCount} icon={<EyeIcon className="w-6 h-6" data-id="element-781" />} iconBgColor="bg-[#FF3B3B]/10" iconColor="text-[#FF3B3B]" borderColor="border-t-[#FF3B3B]" data-id="element-780" />
        <SummaryCard title="Tugas Selesai" value={completedCount} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-783" />} iconBgColor="bg-[#00FF88]/10" iconColor="text-[#00FF88]" borderColor="border-t-[#00FF88]" data-id="element-782" />
      </div>

      {/* Filters */}
      <div className="bg-[#111827] p-4 rounded shadow-sm border border-[#1E3A5F] flex flex-wrap gap-4 items-center" data-id="element-784">
        <div className="relative flex-1 min-w-[200px]" data-id="element-785">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-786">
            <SearchIcon className="h-4 w-4 text-[#94A3B8]" data-id="element-787" />
          </div>
          <input type="text" placeholder="Cari kode, judul, atau subjek target..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-[#1E3A5F] rounded text-sm focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0] placeholder-[#475569]" data-id="element-788" />
        </div>

        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-789">
          <option value="Semua Status" data-id="element-790">Semua Status</option>
          <option value="Draft" data-id="element-791">Draft</option>
          <option value="Direncanakan" data-id="element-792">Direncanakan</option>
          <option value="Berjalan" data-id="element-793">Berjalan</option>
          <option value="Selesai" data-id="element-794">Selesai</option>
          <option value="Dibatalkan" data-id="element-795">Dibatalkan</option>
        </select>

        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border border-[#1E3A5F] rounded text-sm py-2 pl-3 pr-8 focus:ring-1 focus:ring-[#00D4FF] focus:border-[#00D4FF] bg-[#0B0F1A] text-[#E2E8F0]" data-id="element-796">
          <option value="Semua Prioritas" data-id="element-797">Semua Prioritas</option>
          <option value="Rendah" data-id="element-798">Rendah</option>
          <option value="Sedang" data-id="element-799">Sedang</option>
          <option value="Tinggi" data-id="element-800">Tinggi</option>
          <option value="Sangat Tinggi" data-id="element-801">Sangat Tinggi</option>
        </select>
      </div>

      {/* Task List */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-802">
        {filteredTasks.length > 0 ? filteredTasks.map(task => <div key={task.id} className="bg-[#111827] border border-[#1E3A5F] rounded-lg p-5 hover:border-[#00FF88]/50 transition-colors shadow-lg flex flex-col group cursor-pointer" onClick={() => handleView(task.id)} data-id="element-803">
              <div className="flex justify-between items-start mb-3" data-id="element-804">
                <span className="text-xs font-mono text-[#00FF88] bg-[#00FF88]/10 px-2 py-1 rounded" data-id="element-805">
                  {task.code}
                </span>
                <StatusBadge status={task.status as any} data-id="element-806" />
              </div>
              <h3 className="text-lg font-bold text-[#E2E8F0] mb-2 line-clamp-2 group-hover:text-[#00FF88] transition-colors" data-id="element-807">
                {task.title}
              </h3>
              <p className="text-sm text-[#94A3B8] mb-4 line-clamp-2 flex-1" data-id="element-808">
                {task.description}
              </p>

              <div className="space-y-2 mb-4 bg-[#0B0F1A] p-3 rounded border border-[#1E3A5F]/50" data-id="element-809">
                <div className="flex justify-between text-xs items-center" data-id="element-810">
                  <span className="text-[#64748B]" data-id="element-811">Klasifikasi:</span>
                  <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${getClassificationColor(task.classification)}`} data-id="element-812">
                    {task.classification}
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-2" data-id="element-813">
                  <span className="text-[#64748B]" data-id="element-814">Subjek:</span>
                  <span className="text-[#E2E8F0] font-bold" data-id="element-815">
                    {task.subjectName}
                  </span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-816">
                  <span className="text-[#64748B]" data-id="element-817">Tipe:</span>
                  <span className="text-[#E2E8F0] font-medium" data-id="element-818">
                    {task.subjectType}
                  </span>
                </div>
              </div>

              <div className="mb-4" data-id="element-819">
                <div className="text-xs text-[#64748B] mb-1.5 flex items-center" data-id="element-820">
                  <DatabaseIcon className="w-3 h-3 mr-1" data-id="element-821" />
                  Sumber Data:
                </div>
                <div className="flex flex-wrap gap-1.5" data-id="element-822">
                  {task.dataSources.map((source, idx) => <span key={idx} className="text-[10px] bg-[#1E3A5F]/50 text-[#00D4FF] px-1.5 py-0.5 rounded border border-[#1E3A5F]" data-id="element-823">
                      {source}
                    </span>)}
                </div>
              </div>

              <div className="pt-4 border-t border-[#1E3A5F] flex justify-between items-center mt-auto" data-id="element-824">
                <PriorityBadge priority={task.priority} data-id="element-825" />
                <div className="text-xs text-[#94A3B8] flex items-center" data-id="element-826">
                  <EyeIcon className="w-3 h-3 mr-1 text-[#00FF88]" data-id="element-827" />
                  {task.personInCharge}
                </div>
              </div>
            </div>) : <div className="col-span-full py-12 text-center bg-[#111827] border border-[#1E3A5F] rounded" data-id="element-828">
            <GlobeIcon className="w-12 h-12 text-[#1E3A5F] mx-auto mb-3" data-id="element-829" />
            <p className="text-[#94A3B8]" data-id="element-830">
              Tidak ada tugas OSINT yang ditemukan.
            </p>
          </div>}
      </div>
    </div>;
}